// src/charts/heating/PowerChart_V2.js

import React, { useMemo, useRef, useEffect, useState } from 'react';
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts';
import { InfoBox } from '../../Components';
import {
  extractTimeseries,
  aggregateToHourly,
  getHourlyTicks,
  formatHourLabel,
  hasMetering,
  isMobileLayout
} from './heatingChartUtils';
import { t } from '../../i18n';

/**
 * PowerChart_V2 - Displays power (kW) consumption over time
 *
 * Two modes:
 * 1. Metered data: Shows metered power from whole-house metering
 * 2. Habitaio controlled only: Shows consumption power from controlled devices
 */
const PowerChart_V2 = ({ data, metadata }) => {
  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [showDetails, setShowDetails] = useState(false); // Mobile: Show 15-min details

  // Extract timeseries
  const timeseries = useMemo(() => extractTimeseries(data), [data]);

  // Check if metering is available
  const hasMeteredData = useMemo(() => hasMetering(data), [data]);

  // Determine if mobile layout
  const isMobile = useMemo(() => isMobileLayout(containerWidth), [containerWidth]);

  // Process data for chart (15-min or hourly based on mobile + toggle)
  const processedData = useMemo(() => {
    const shouldAggregate = isMobile && !showDetails;
    const source = shouldAggregate ? aggregateToHourly(timeseries) : timeseries;

    return source.map(snapshot => {
      if (hasMeteredData && snapshot.metered?.power) {
        // Metered data available - show metered power (converted to kW)
        return {
          timestamp: snapshot.timestamp,
          power: (snapshot.metered.power?.avg || 0) / 1000  // Convert W to kW
        };
      } else {
        // No metered data - show consumption power (converted to kW)
        return {
          timestamp: snapshot.timestamp,
          power: (snapshot.consumption?.power_avg || 0) / 1000  // Convert W to kW
        };
      }
    });
  }, [timeseries, isMobile, showDetails, hasMeteredData]);

  // Get tick marks (hourly)
  const hourlyTicks = useMemo(() => {
    if (isMobile && !showDetails) {
      // Show fewer ticks on mobile hourly view
      return processedData
        .filter((_, index) => index % 2 === 0) // Every 2 hours
        .map(d => d.timestamp);
    }
    return getHourlyTicks(processedData);
  }, [processedData, isMobile, showDetails]);

  // Calculate statistics for InfoBox
  const powerStats = useMemo(() => {
    if (processedData.length === 0) return null;

    const powerValues = processedData.map(d => d.power).filter(v => v > 0);

    return {
      powerMax: powerValues.length > 0 ? Math.max(...powerValues) : 0,
      powerAvg: powerValues.length > 0 ? powerValues.reduce((a, b) => a + b, 0) / powerValues.length : 0,
      powerMin: powerValues.length > 0 ? Math.min(...powerValues) : 0
    };
  }, [processedData]);

  // Update container width
  useEffect(() => {
    const updateWidth = () => {
      if (chartRef.current) {
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        if (wrapper) {
          setContainerWidth(wrapper.getBoundingClientRect().width);
        }
      }
    };

    const timer = setTimeout(updateWidth, 100);
    window.addEventListener('resize', updateWidth);

    return () => {
      window.removeEventListener('resize', updateWidth);
      clearTimeout(timer);
    };
  }, []);

  // InfoBox data
  const infoBoxData = useMemo(() => {
    if (!powerStats) return [];

    return [
      { label: t('heatingChart.powerMax'), value: `${powerStats.powerMax.toFixed(2)} kW` },
      { label: t('heatingChart.powerAvg'), value: `${powerStats.powerAvg.toFixed(2)} kW` },
      { label: t('heatingChart.powerMin'), value: `${powerStats.powerMin.toFixed(2)} kW` }
    ];
  }, [powerStats]);

  // Custom legend
  const customLegend = useMemo(() => {
    return [
      { value: t('heatingChart.power'), type: 'line', color: '#4169E1' }
    ];
  }, []);

  return (
    <>
      <div className="center-text">
        <h3>{t('heatingChart.powerConsumption')}</h3>
      </div>

      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={processedData}
            margin={{ top: 20, right: 60, bottom: 20, left: 20 }}
          >
            <CartesianGrid stroke="#f5f5f5" />

            <XAxis
              dataKey="timestamp"
              name="Hour"
              tickFormatter={formatHourLabel}
              ticks={hourlyTicks}
              interval={0}
              tick={{ fontSize: 12 }}
              height={40}
            />

            {/* Y-axis for Power (kW) */}
            <YAxis
              label={{ value: t('heatingChart.power') + ' (kW)', angle: -90, position: 'insideLeft' }}
            />

            <Tooltip
              formatter={(value, name) => {
                return [`${Number(value).toFixed(2)} kW`, name];
              }}
            />

            <Legend payload={customLegend} />

            {/* Power line */}
            <Line
              type="monotone"
              dataKey="power"
              name={t('heatingChart.power')}
              stroke="#4169E1"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Mobile toggle */}
      {isMobile && (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <button onClick={() => setShowDetails(!showDetails)}>
            {showDetails ? 'Show Hourly View' : 'Show 15-min Details'}
          </button>
        </div>
      )}

      {/* InfoBox */}
      <div>
        <InfoBox
          title={t('heatingChart.powerStats')}
          dataPoints={infoBoxData}
        />
      </div>

      <p></p>
    </>
  );
};

export default PowerChart_V2;
